/**
 * 보증금 — 한 메뉴 두 탭(명세 EA-14·EA-16)과 파트너 상세(EA-15), 철회 승인(EA-17).
 *
 * 계약: XM-43(파트너 목록)·XM-44(상세)·XM-47~XM-49(철회).
 *
 * ⚠ **정원 변경 탭(옛 EA-46~EA-48 — 승인·거절)은 없다.** 보증금은 단계 증가만 있고 낮추는
 * 기능은 없다(원문 3). 단계 올리기는 실제 잔액이 새 단계 금액에 닿으면 자동이다(확인 15,
 * 확인 30 Q-34) — 사람이 승인할 것이 없다. R-XAD-02.
 *
 * ⚠ **충전 탭은 없다.** 충전은 체인에서 확인되면 서버가 자동 반영한다 — 사람의 승인·거절·
 * 되돌리기가 없다(거래소 01 9-1 16번 확정 2026-09-23, 계약 10-2). 옛 충전 탭 자리는 파트너
 * 목록이고, 충전 기록은 파트너 상세의 `charges` 로 본다(명세 EA-14·EA-15).
 *
 * ⚠ **탭은 키대로 그린다.** 파트너는 `collateral.view`, 철회는 `withdrawals.view`.
 * 탭은 주소에 싣지 않고, 탭을 바꾸면 쪽 값만 지운다(명세 2-7, 레거시 그대로). 탭마다
 * 상태 거르개의 값이 다르므로 주소 이름을 탭마다 따로 둔다 — 한 탭의 상태가 다른 탭
 * 창구로 새어 나가지 않게.
 *
 * ⚠ **금액은 계약이 준 문자열 그대로**(내부 단위, `formatUsde`). 이 파일은 계산하지 않는다.
 *
 * 2026-09-30 고침(R-XAD-03 — 원문 4, 확인 13·16·19, api-XB XM-43·XM-44·XM-46): 사용료는 월 10,000 USDE
 * 선납이다. 「오늘 사용료」(날마다 후불) 대신 설정 단계·넣은 합·월 사용료(낸 기간 끝·다음 차감·멈춘
 * 시간)·다음 단계를 보이고, 파트너 상세에 월 사용료 기록(XM-46 `fee-months`)을 보인다.
 *
 * 2026-10-12 고침(사장님 2026-10-11 「사용료차감일날 거래횟수 수수료도 같이 차감되는 월 사용료다」 「과금구간은 100건당 10usde」
 * 「거래소 관리자에서 수정할수 있도록」 — 거래소백엔드 V542): 월 사용료 = 고정 + 거래 횟수 수수료. 상세의 월 사용료 줄에 지금 기간의
 * 거래 횟수 수수료·다음 차감 금액(지금까지)·마지막 달 나눔, 사용료 기록 줄마다 고정·거래 수·거래 횟수 수수료. 요율(구간 건수·구간
 * 금액)은 시스템 설정에서 고친다(`fee.trade_block_size`·`fee.trade_block_fee`).
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  approveWithdrawal, getPartner, getWithdrawal, listFeeMonths, listPartners, listWithdrawals,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  Button, Card, Code, Labeled, LINK, Maybe, MemberLink, Notice, NUM, Row, Tabs, TD, TH, Time,
} from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { ApiError } from '@/api/errors'
import { QueryState } from '@/components/QueryState'
import { OffsetPager } from '@/components/Pager'
import { Choice, FilterBar, optionsOf, useFilters } from '@/components/Filters'
import { formatBp, formatDuration, formatTime, formatWhen, usde } from '@/format'
import {
  FEE_VIA_LABEL, PARTNER_STATUS_LABEL, WARNING_LEVEL_LABEL, WITHDRAWAL_STATUS_LABEL,
  type ChainOperation, type MonthlyFee, type SettlementValues, type TierChoice, type Withdrawal,
} from '@/api/types'

/** 설정 단계 한 줄 — 「50명 · 100,000.00 USDE」. 없으면 null. */
export function tierText(t: TierChoice | null | undefined): string | null {
  if (!t) return null
  return `${t.members.toLocaleString('en-US')} members · ${usde(t.deposit) ?? t.deposit}`
}

const LIMIT = 25

type Tab = 'partners' | 'withdrawals'

/* ── 보증금 화면 · 탭 ─────────────────────────────────────────────── */

export function CollateralPage() {
  const { has } = useSession()
  const f = useFilters(TAB_FILTERS)

  const tabs: { kind: Tab; label: string }[] = []
  if (has('collateral.view')) tabs.push({ kind: 'partners', label: 'Partners' })
  if (has('withdrawals.view')) tabs.push({ kind: 'withdrawals', label: 'Withdrawals' })

  // 사장님 2026-10-15: 박탈 창(EA-19·EA-21)의 길 `?tab=withdrawals` 은 철회 탭으로 연다(그 탭을 볼 수 있을 때만).
  const [params] = useSearchParams()
  const asked = params.get('tab')
  const [active, setActive] = useState<Tab>(
    tabs.some((t) => t.kind === asked) ? (asked as Tab) : (tabs[0]?.kind ?? 'partners'))

  function switchTo(kind: Tab) {
    if (kind === active) return
    // 다른 목록으로 넘어가면 쪽 값은 뜻을 잃는다.
    f.resetPaging()
    setActive(kind)
  }

  return (
    <Card title="Collateral">
      <Tabs tabs={tabs} active={active} onSelect={switchTo} />
      {active === 'partners' && <PartnersTab />}
      {active === 'withdrawals' && <WithdrawalsTab />}
    </Card>
  )
}

const TAB_FILTERS: readonly string[] = []

/* ── EA-14 파트너 탭 (XM-43) ─────────────────────────────────────── */

const PARTNER_FILTERS = ['partnerStatus'] as const

function PartnersTab() {
  const f = useFilters(PARTNER_FILTERS)
  const params = { status: f.values['partnerStatus'] || undefined, offset: f.offset, limit: LIMIT }
  const q = useQuery({ queryKey: ['partners', params], queryFn: () => listPartners(params) })

  return (
    <>
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Status" value={f.values['partnerStatus'] ?? ''}
          options={optionsOf(PARTNER_STATUS_LABEL)} onChange={(v) => f.set('partnerStatus', v)} />
      </FilterBar>
      <QueryState isPending={q.isPending} error={q.error} empty={q.data?.length === 0}
        emptyText="No partners match the filters.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Master</th><th className={TH}>Status</th>
            <th className={TH}>Collateral held</th><th className={TH}>Tier</th>
            <th className={TH}>Warning level</th><th className={TH}>Drained rate</th>
            <th className={TH}>Next fee deduction</th>
            <th className={TH}>First deposit deadline</th><th className={TH}>Referral code</th>
          </tr></thead>
          <tbody>
            {(q.data ?? []).map((p) => (
              <tr key={p.partnerId}>
                <td className={TD}>
                  <Link className={LINK} to={`/collateral/partners/${p.partnerId}`}>{p.partnerId}</Link>
                </td>
                <td className={TD}><MemberLink memberId={p.memberId} loginId={p.loginId} /></td>
                <td className={TD}><Labeled labels={PARTNER_STATUS_LABEL} code={p.status} /></td>
                <td className={NUM}>
                  <Maybe value={usde(p.lastBalance)} />
                  {p.lastBalanceReadAt !== null && (
                    <div className="text-xs text-slate-500">{formatTime(p.lastBalanceReadAt)}</div>
                  )}
                </td>
                {/* 설정 단계(확인 31·32). 새 판 전 기록이면 옛 정원수당 보증금. */}
                <td className={NUM}><Maybe value={tierText(p.tier) ?? usde(p.capacityDeposit)} /></td>
                <td className={`${TD} ${p.warningLevel >= 3 ? 'font-semibold text-red-700 dark:text-red-400' : ''}`}>
                  <Labeled labels={WARNING_LEVEL_LABEL} code={p.warningLevel} />
                </td>
                <td className={NUM}><Maybe value={formatBp(p.drainedBp)} /></td>
                <td className={TD}><Time ms={p.monthlyFee?.nextDeductionAt ?? null} /></td>
                <td className={TD}><Time ms={p.firstDepositDeadline} /></td>
                <td className={TD}><Maybe value={p.referralCode} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <OffsetPager offset={f.offset} limit={LIMIT} count={q.data?.length} onChange={f.setOffset} />
    </>
  )
}

/* ── EA-15 파트너 보증금 상세 (XM-44) ────────────────────────────── */

export function PartnerDetailPage() {
  const { partnerId: raw = '' } = useParams()
  const partnerId = Number(raw)
  const q = useQuery({ queryKey: ['partner', partnerId], queryFn: () => getPartner(partnerId) })
  const d = q.data

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/collateral" className="text-xs text-slate-500 underline">← Collateral</Link>
      </div>
      <Card title={d ? `Partner #${d.partner.partnerId} · ${d.partner.loginId}` : `Partner #${raw}`}>
        <QueryState isPending={q.isPending} error={q.error}>
          {d && (
            <dl className="grid grid-cols-[11rem_1fr] gap-2 text-sm">
              <Row label="Master"><MemberLink memberId={d.partner.memberId} loginId={d.partner.loginId} /></Row>
              <Row label="Status"><Labeled labels={PARTNER_STATUS_LABEL} code={d.partner.status} /></Row>
              <Row label="Collateral contract">
                <span className="break-all font-mono text-xs"><Maybe value={d.collateral.collateralContract} /></span>
                {d.collateral.contractStatus && <span className="ml-2"><Code>{d.collateral.contractStatus}</Code></span>}
              </Row>
              <Row label="Balance">
                <span className="font-mono tabular-nums"><Maybe value={usde(d.collateral.balance.amount)} /></span>
                <span className="ml-2 text-xs text-slate-500">
                  {d.collateral.balance.readAt !== null && `Read at ${formatTime(d.collateral.balance.readAt)}`}
                  {d.collateral.balance.live ? ' · just read from the chain' : ' · stored value'}
                </span>
              </Row>
              <Row label="Tier">
                <span className="font-mono"><Maybe value={tierText(d.collateral.tier ?? d.partner.tier)} /></span>
              </Row>
              <Row label="Total deposited">
                <span className="font-mono"><Maybe value={usde(d.collateral.depositedTotal ?? d.partner.depositedTotal ?? null)} /></span>
              </Row>
              <MonthlyFeeRows fee={d.collateral.monthlyFee ?? d.partner.monthlyFee ?? null} />
              <Row label="Next tier">
                {d.collateral.nextTier
                  ? <span className="font-mono">
                      {tierText(d.collateral.nextTier)}
                      <span className="ml-2 text-xs text-slate-500">
                        Shortfall <Maybe value={usde(d.collateral.nextTier.shortfall)} /> — moves up automatically when the balance reaches it
                      </span>
                    </span>
                  : <Maybe value={null} />}
              </Row>
              <Row label="Minimum first deposit"><span className="font-mono"><Maybe value={usde(d.collateral.minFirstDeposit)} /></span></Row>
              <Row label="Shortfall"><span className="font-mono"><Maybe value={usde(d.collateral.firstDepositShortfall)} /></span></Row>
              <Row label="Suspension floor"><span className="font-mono"><Maybe value={usde(d.collateral.floorAmount)} /></span></Row>
              <Row label="Capacity">
                {d.collateral.members.held} / <Maybe value={d.collateral.members.max} />
              </Row>
              <Row label="Suspended">
                {d.collateral.suspended
                  ? <span className="text-red-700 dark:text-red-400">Suspended · <Maybe value={d.collateral.suspendedReason} /></span>
                  : 'No'}
              </Row>
              <Row label="Warning">
                <Labeled labels={WARNING_LEVEL_LABEL} code={d.collateral.warning.level} />
                {d.collateral.warning.message && <span className="ml-2">{d.collateral.warning.message}</span>}
              </Row>
              <Row label="Drained rate"><Maybe value={formatBp(d.collateral.drainedBp)} /></Row>
              <Row label="First deposit deadline"><Time ms={d.collateral.firstDepositDeadline} /></Row>
            </dl>
          )}
        </QueryState>
      </Card>

      {d && (
        <Card title="Top-ups">
          {d.charges.length === 0
            ? <p className="text-sm text-slate-500">No top-up records.</p>
            : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className={TH}>No.</th><th className={TH}>Amount</th><th className={TH}>Status</th>
                  <th className={TH}>Expires</th><th className={TH}>Result</th><th className={TH}>Confirmed at</th>
                  <th className={TH}>Confirmed amount</th><th className={TH}>Created at</th>
                </tr></thead>
                <tbody>
                  {d.charges.map((c) => (
                    <tr key={c.requestId}>
                      <td className={`${TD} font-mono text-xs`}>{c.requestId}</td>
                      <td className={NUM}>{usde(c.amount)}</td>
                      <td className={TD}><Code>{c.status}</Code></td>
                      <td className={TD}><Time ms={c.expiresAt} /></td>
                      <td className={TD}>
                        {c.resultStatus ? <Code>{c.resultStatus}</Code> : <Maybe value={null} />}
                        {c.resultTxHash && <div className="break-all font-mono text-xs">{c.resultTxHash}</div>}
                      </td>
                      <td className={TD}><Time ms={c.confirmedAt} /></td>
                      <td className={NUM}><Maybe value={usde(c.confirmedAmount)} /></td>
                      <td className={TD}><Time ms={c.createdAt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      )}

      {d && <FeeMonthsCard partnerId={partnerId} />}

      {d && (
        <Card title="Chain operations">
          {d.chainOperations.length === 0
            ? <p className="text-sm text-slate-500">No chain operations.</p>
            : (
              <table className="w-full text-sm">
                <thead><tr>
                  <th className={TH}>Kind</th><th className={TH}>Status</th><th className={TH}>txHash</th>
                  <th className={TH}>Attempts</th><th className={TH}>Error name</th><th className={TH}>Time</th>
                </tr></thead>
                <tbody>
                  {d.chainOperations.map((o) => (
                    <tr key={o.id}>
                      <td className={TD}><Code>{o.kind}</Code></td>
                      <td className={TD}>
                        <Code>{o.status}</Code>
                        {o.nextTryAt != null && <NextTry ms={o.nextTryAt} />}
                      </td>
                      <td className={`${TD} break-all font-mono text-xs`}><Maybe value={o.txHash} /></td>
                      <td className={TD}>{attemptsText(o.attempts, o.failures ?? 0)}</td>
                      <td className={TD}><OperationError op={o} /></td>
                      <td className={TD}><Time ms={o.finishedAt ?? o.sentAt ?? o.createdAt} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </Card>
      )}
    </div>
  )
}

/*
 * 발견-410 (데브넷 2026-10-15, X3 철회 5): 정산(SETTLE)이 가스 면제 상한에 막혀 거래소가 다시 시도하는 동안 철회 탭 정산 칸은
 * 'PENDING' 만, 체인 작업 표는 'Attempts 0'(서버 `attempts` 는 서명해 보낸 횟수 — 서명 전에 막힌 시도는 세지 않음), 나간 뒤에도
 * 'CONFIRMED · EXEMPT_GAS_CAP'. 서버가 가진 값(XM-47 `settlement`·XM-44 `chainOperations` 의 `failures`·`nextTryAt`·`lastError` —
 * CHAIN_OP_FAILED 알림의 failures·nextTry 와 같은 값)으로 보인다. 이 파일은 값을 셈하지 않고 서버 값 그대로 쓴다.
 */

/** 체인 작업 시도 칸 — 실패가 없으면 전과 같이 보낸 수, 있으면 「0 sent · 3 failed」. */
export function attemptsText(attempts: number, failures: number): string {
  return failures > 0 ? `${attempts} sent · ${failures} failed` : String(attempts)
}

/** 「Failed 3 times · retrying」(1 이면 time). */
export function retryingText(failures: number): string {
  return `Failed ${failures} ${failures === 1 ? 'time' : 'times'} · retrying`
}

function NextTry({ ms }: { ms: number }) {
  return <div className="text-xs text-slate-500">Next try {formatTime(ms)}</div>
}

/** 지금의 까닭 — 오류 이름(코드)과 서버 글. */
function FailureReason({ errorName, lastError }: { errorName: string | null; lastError: string | null }) {
  return (
    <>
      {errorName && <div><Code>{errorName}</Code></div>}
      {lastError && <div className="break-all text-xs text-slate-500">{lastError}</div>}
    </>
  )
}

/**
 * 체인 작업 오류 칸. 기다리는(PENDING)·실패로 끝난(FAILED) 작업은 지금의 까닭(오류 이름·글). 나간 뒤(SENT·CONFIRMED)나 취소된
 * 작업에 남은 오류 이름은 지난 일이라 앞에 「Last failure」.
 */
function OperationError({ op }: { op: ChainOperation }) {
  const current = op.status === 'PENDING' || op.status === 'FAILED'
  if (current && (op.errorName || op.lastError)) {
    return <FailureReason errorName={op.errorName} lastError={op.lastError} />
  }
  if (!current && op.errorName) {
    return <span className="text-xs text-slate-500">Last failure <Code>{op.errorName}</Code></span>
  }
  return <Maybe value={null} />
}

/**
 * EA-16 정산 칸 — 상태·txHash 는 전과 같다. 실패 뒤 다시 시도를 기다리면(PENDING · failures > 0) 실패 수·까닭·다음 시도 시각,
 * 실패로 끝났으면(FAILED) 까닭을 더한다. 나간 뒤(SENT·CONFIRMED)에 남은 옛 오류 이름은 이 칸에서 감춘다 — 지난 실패는 파트너 상세
 * 체인 작업 표의 「Last failure」. 새 칸이 없는 옛 서버 답이면 전과 같이 상태·txHash 만.
 */
function SettlementCell({ s }: { s: Withdrawal['settlement'] }) {
  if (!s?.status) return <Maybe value={null} />
  const failures = s.failures ?? 0
  const retrying = s.status === 'PENDING' && failures > 0
  return (
    <>
      <Code>{s.status}</Code>
      {s.txHash && <div className="break-all font-mono text-xs">{s.txHash}</div>}
      {retrying && (
        <div className="text-xs font-semibold text-red-700 dark:text-red-400">{retryingText(failures)}</div>
      )}
      {(retrying || s.status === 'FAILED') && <FailureReason errorName={s.errorName} lastError={s.lastError ?? null} />}
      {retrying && s.nextTryAt != null && <NextTry ms={s.nextTryAt} />}
    </>
  )
}

/** 「10.00 USDE per started 100 trades」 — 거래 횟수 수수료 요율 한 줄. 없으면 null. */
export function blockRateText(size: number | null | undefined, blockFee: string | null | undefined): string | null {
  if (size === null || size === undefined || blockFee === null || blockFee === undefined) return null
  return `${usde(blockFee) ?? blockFee} per started ${size.toLocaleString('en-US')} trades`
}

/**
 * 월 사용료 줄(R-XAD-03). 2026-10-12 고정 + 거래 횟수 수수료(사장님 2026-10-11 「사용료차감일날 거래횟수 수수료도 같이 차감되는
 * 월 사용료다」 「과금구간은 100건당 10usde」 — 체크리스트 H1-08 · H2-07, api-XB XA-46 `monthlyFee`): 고정 부분, 지금 기간의 거래
 * 횟수 수수료(회원 완료 거래 수 · 지금 요율), 기간이 지금 끝나면 다음 차감 금액, 마지막으로 시작한 달의 나눔, 그리고 전과 같은 낸
 * 기간 끝·다음 차감·멈춘 시간. 금액은 서버가 준 문자열 그대로(이 파일은 계산하지 않는다). 거래 칸이 없는 옛 서버 답이면 고정·날짜
 * 줄만. 없으면 한 줄 —.
 */
function MonthlyFeeRows({ fee }: { fee: MonthlyFee | null }) {
  if (!fee) return <Row label="Monthly fee"><Maybe value={null} /></Row>
  const parts = fee.periodTradeCount !== undefined
  const last = fee.lastMonth ?? null
  return (
    <>
      <Row label="Monthly fee">
        <span className="font-mono"><Maybe value={usde(fee.amount)} /></span>{' '}
        <span className="text-xs text-slate-500">{parts ? '(fixed part, prepaid)' : '(prepaid)'}</span>
      </Row>
      {parts && (
        <>
          <Row label="Trade-count fee">
            <span className="font-mono"><Maybe value={usde(fee.periodTradeFee ?? null)} /></span>
            <span className="ml-2 text-xs text-slate-500">
              {fee.periodTradeCount === null || fee.periodTradeCount === undefined
                ? 'Completed trades this period: —'
                : `${fee.periodTradeCount.toLocaleString('en-US')} completed trades of the members this period`}
              {fee.periodStart !== null && fee.periodStart !== undefined && ` (since ${formatTime(fee.periodStart)})`}
              {blockRateText(fee.tradeBlockSize, fee.tradeBlockFee) && ` · ${blockRateText(fee.tradeBlockSize, fee.tradeBlockFee)}`}
            </span>
          </Row>
          <Row label="Next deduction amount">
            <span className="font-mono"><Maybe value={usde(fee.nextAmount ?? null)} /></span>{' '}
            <span className="text-xs text-slate-500">(fixed + trade-count fee so far)</span>
          </Row>
          <Row label="Last month">
            {last
              ? <span className="font-mono text-xs">
                  #{last.seq} · {usde(last.fixedFee) ?? last.fixedFee} + {usde(last.tradeFee) ?? last.tradeFee}
                  {' '}({last.tradeCount.toLocaleString('en-US')} trades) = {usde(last.total) ?? last.total}
                  {' '}<Code>{last.status}</Code>
                </span>
              : <Maybe value={null} />}
          </Row>
        </>
      )}
      <Row label="Paid through"><Time ms={fee.paidThrough} /></Row>
      <Row label="Next deduction"><Time ms={fee.nextDeductionAt} /></Row>
      <Row label="Paused time"><Maybe value={formatDuration(fee.pausedMs)} /></Row>
    </>
  )
}

/**
 * 파트너 상세 — 월 사용료 기록(XM-46 고침 `fee-months`). 못 읽어도 상세는 그대로. 2026-10-12: 줄마다 나눔 — 고정·거래 수
 * (그 달 시작 전 기간의 회원 완료 거래, 셈 시작 시각)·거래 횟수 수수료(그 달이 시작할 때의 요율)·합계(`amount`, 뺀 금액).
 * 첫 달은 거래 0·요율 없음. 나눔 칸이 없는 옛 서버 답이면 「—」.
 */
function FeeMonthsCard({ partnerId }: { partnerId: number }) {
  const q = useQuery({ queryKey: ['fee-months', partnerId], queryFn: () => listFeeMonths(partnerId) })
  const rows = q.data ?? []
  return (
    <Card title="Monthly fee records">
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="No fee records.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Period start</th><th className={TH}>Due date</th>
            <th className={TH}>Fixed fee</th><th className={TH}>Trades</th><th className={TH}>Trade-count fee</th>
            <th className={TH}>Amount</th><th className={TH}>Status</th><th className={TH}>How</th>
            <th className={TH}>Chain date</th><th className={TH}>Paid at</th>
          </tr></thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.seq}>
                <td className={TD}>{m.seq}</td>
                <td className={TD}><Maybe value={formatWhen(m.periodStart)} /></td>
                <td className={TD}><Maybe value={formatWhen(m.dueAt)} /></td>
                <td className={NUM}><Maybe value={usde(m.fixedFee ?? null)} /></td>
                <td className={NUM}>
                  {m.tradeCount === null || m.tradeCount === undefined
                    ? <Maybe value={null} />
                    : m.tradeCount.toLocaleString('en-US')}
                  {m.tradesFrom !== null && m.tradesFrom !== undefined && (
                    <div className="text-xs text-slate-500">since {formatTime(m.tradesFrom)}</div>
                  )}
                </td>
                <td className={NUM}>
                  <Maybe value={usde(m.tradeFee ?? null)} />
                  {blockRateText(m.tradeBlockSize, m.tradeBlockFee) && (
                    <div className="text-xs text-slate-500">{blockRateText(m.tradeBlockSize, m.tradeBlockFee)}</div>
                  )}
                </td>
                <td className={NUM}><Maybe value={usde(m.amount)} /></td>
                <td className={TD}><Code>{m.status}</Code></td>
                <td className={TD}>{m.via ? <Labeled labels={FEE_VIA_LABEL} code={m.via} /> : <Maybe value={null} />}</td>
                <td className={TD}><Maybe value={m.chainDate} /></td>
                <td className={TD}><Time ms={m.paidAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </Card>
  )
}

/* ── EA-16 철회 탭 (XM-47) ───────────────────────────────────────── */

const WITHDRAWAL_FILTERS = ['withdrawalStatus'] as const

function WithdrawalsTab() {
  const f = useFilters(WITHDRAWAL_FILTERS)
  const params = { status: f.values['withdrawalStatus'] || undefined, offset: f.offset, limit: LIMIT }
  const q = useQuery({ queryKey: ['withdrawals', params], queryFn: () => listWithdrawals(params) })
  // 발견-627: 승인 창은 줄 밖에 둔다 — 409 뒤 목록을 다시 읽어 줄이 Approved(또는 대기 거르개에서 빠짐)가 되어도
  // 창과 창 안 빨간 글이 남는다(줄 안에 두면 [Approve] 와 함께 창도 사라진다).
  const [approving, setApproving] = useState<Withdrawal | null>(null)

  return (
    <>
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Status" value={f.values['withdrawalStatus'] ?? ''}
          options={optionsOf(WITHDRAWAL_STATUS_LABEL)} onChange={(v) => f.set('withdrawalStatus', v)} />
      </FilterBar>
      <QueryState isPending={q.isPending} error={q.error} empty={q.data?.length === 0}
        emptyText="No requests match the filters.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Master</th><th className={TH}>Status</th>
            <th className={TH}>Requested at</th><th className={TH}>Approval deadline</th>
            <th className={TH}>Collateral</th><th className={TH}>Fees due</th><th className={TH}>Penalty</th>
            <th className={TH}>Refund</th><th className={TH}>Demoted specials</th>
            <th className={TH}>Settlement</th><th className={TH}>Actions</th>
          </tr></thead>
          <tbody>
            {(q.data ?? []).map((r) => (
              <tr key={r.withdrawalId}>
                <td className={TD}>{r.withdrawalId}</td>
                <td className={TD}><MemberLink memberId={r.memberId} loginId={r.loginId} /></td>
                <td className={TD}><Labeled labels={WITHDRAWAL_STATUS_LABEL} code={r.status} /></td>
                <td className={TD}><Time ms={r.requestedAt} /></td>
                <td className={TD}>
                  <Time ms={r.approvalDeadlineAt} />
                  {r.overdue && (
                    <span className="ml-2 font-semibold text-red-700 dark:text-red-400">Overdue</span>
                  )}
                </td>
                <td className={NUM}><WithdrawalAmount row={r} field="balance" /></td>
                <td className={NUM}><WithdrawalAmount row={r} field="feesDue" /></td>
                <td className={NUM}><WithdrawalAmount row={r} field="penalty" /></td>
                <td className={NUM}><WithdrawalAmount row={r} field="refund" /></td>
                <td className={NUM}><Maybe value={r.demotedCount?.toLocaleString('en-US')} /></td>
                <td className={TD}><SettlementCell s={r.settlement} /></td>
                <td className={TD}><ApproveWithdrawalButton row={r} onOpen={() => setApproving(r)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <OffsetPager offset={f.offset} limit={LIMIT} count={q.data?.length} onChange={f.setOffset} />
      {approving && <ApproveWithdrawalDialog row={approving} onClose={() => setApproving(null)} />}
    </>
  )
}

/**
 * Keep requested amounts for comparison; approved amounts must never fall back to estimates. 발견-604 (2026-10-20 — 관01 0-6):
 * '90,010.00 USDE' like the other tables (before: the number alone).
 */
function WithdrawalAmount({ row, field }: { row: Withdrawal; field: 'balance' | 'feesDue' | 'penalty' | 'refund' }) {
  return <>
    <Maybe value={usde(row.requested?.[field] ?? null)} />
    {row.status === 'APPROVED' && (
      <div className="text-xs text-slate-500">Approved: {usde(row.approved?.[field] ?? null) ?? '—'}</div>
    )}
  </>
}

/* ── EA-17 보증금 철회 승인 창 (XM-48 → XM-49) ───────────────────── */

/**
 * ⚠ **돈이 움직인다.** 창을 열 때 XM-48 로 **지금 잔액으로 다시 계산한 값**을 받아
 * 신청 때 값과 나란히 보인다. 다시 계산한 값이 없으면(체인을 못 읽음) 「승인」을 잠근다
 * (제안 03 4번) — 사람이 모르는 금액으로 정산을 보내지 않게.
 */
function ApproveWithdrawalButton({ row, onOpen }: { row: Withdrawal; onOpen: () => void }) {
  const { has } = useSession()

  if (!has('withdrawals.approve')) return null
  // ⚠ 대기 중인 것에만 — 이미 처리된 줄에 단추를 두면 눌러도 되는 것처럼 보인다.
  if (row.status !== 'PENDING') return null

  return <Button onClick={onOpen}>Approve</Button>
}

function ApproveWithdrawalDialog({ row, onClose }: { row: Withdrawal; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const detail = useQuery({
    queryKey: ['withdrawal', row.withdrawalId],
    queryFn: () => getWithdrawal(row.withdrawalId),
    // 창을 열 때마다 새로 계산한다 — 10초 전 값으로 돈을 보내지 않는다.
    staleTime: 0,
    gcTime: 0,
  })
  const d = detail.data
  const recomputed = d?.recomputed ?? null
  // 발견-627: 다시 읽은 상태가 대기가 아니면(다른 사람이 먼저 승인) 승인을 잠근다.
  const decided = d !== undefined && d.status !== 'PENDING'

  async function submit() {
    const send = async () => {
      await approveWithdrawal(row.withdrawalId)
      setFlash({
        tone: 'ok',
        text: `Approved the withdrawal of ${row.loginId}. The settlement shows in the Settlement column of the withdrawal list once it is confirmed on the chain.`,
      })
      // ⚠ 이 승인이 바꾸는 것만 다시 읽는다 — 철회 목록, 마스터와 산하의 등급.
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['withdrawals'] }),
        qc.invalidateQueries({ queryKey: ['partners'] }),
        qc.invalidateQueries({ queryKey: ['member', row.memberId] }),
        qc.invalidateQueries({ queryKey: ['grade-history', row.memberId] }),
        qc.invalidateQueries({ queryKey: ['members'] }),
      ])
    }
    await submitWrite(w, 'Approve withdrawal', send, onClose, {
      // 발견-627: 409(already_reviewed — 다른 사람이 먼저 승인 등)면 화면이 낡았다 — 창의 상태와 뒤 목록을 다시 읽는다.
      // 창은 닫지 않는다(빨간 글을 남긴다).
      onError: (err) => {
        if (!(err instanceof ApiError && err.status === 409)) return
        void qc.invalidateQueries({ queryKey: ['withdrawal', row.withdrawalId] })
        void qc.invalidateQueries({ queryKey: ['withdrawals'] })
      },
    })
  }

  return (
    <Confirm open title="Approve collateral withdrawal" action="Approve" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={detail.isPending || recomputed === null || decided}>
      <dl className="grid grid-cols-[7rem_1fr] gap-1.5">
        <dt className="text-slate-500">Request no.</dt><dd>{row.withdrawalId}</dd>
        <dt className="text-slate-500">Master</dt><dd>{row.loginId}</dd>
        <dt className="text-slate-500">Status</dt>
        <dd><Labeled labels={WITHDRAWAL_STATUS_LABEL} code={d?.status ?? row.status} /></dd>
      </dl>

      <QueryState isPending={detail.isPending} error={detail.error}>
        <SettlementTable
          columns={[
            { label: 'At request', values: d?.requested ?? row.requested },
            { label: 'Recalculated with the current balance', values: recomputed },
          ]} />
        {/* 발견-629: 이미 결정된 건은 서버가 다시 계산하지 않는다(recomputed 없음) — 체인 탓이 아니니 안내를 띄우지 않는다. */}
        {recomputed === null && !decided && (
          <div className="mt-3">
            <Notice tone="warn">The current balance could not be read from the chain, so there are no recalculated values. This cannot be approved.</Notice>
          </div>
        )}
      </QueryState>

      {/*
        정할 것 56 (사장님 2026-10-13 「3」·2026-10-14 「ㅇ」): 보증금 컨트랙트(code 31)는 나가는 금액(위약금·남은 사용료·돌려줄 돈)마다
        그 안에서 전송 수수료 0.17%·가스 0.1% 를 떼고, 서버의 Refund 는 마스터 지갑에 닿는 금액이다(거래소 서버 242f5a5).
      */}
      <p className="mt-3 text-xs text-slate-500">
        Once approved, the master and all special members under them become general members. The penalty and the
        remaining fees are taken from the collateral contract first, and the rest is returned to the master's
        registered wallet. The transfer fee (0.17%) and gas (0.1%) are taken inside each amount the contract sends
        out, so the Refund shown is what reaches the master's wallet. The amounts are recalculated with the balance
        at the time of approval. Monthly fees already paid are not returned.
      </p>
    </Confirm>
  )
}

/**
 * 정산 네 값의 표 — 열마다 한 묶음(철회 EA-17, 박탈 EA-21). 발견-604 뒤따름 (2026-10-21 — 관01 0-6):
 * '90,010.00 USDE' like the withdrawal list (before: the number alone).
 */
export function SettlementTable({ columns }: {
  columns: { label: string; values: SettlementValues | null | undefined }[]
}) {
  const rows: { label: string; key: keyof SettlementValues }[] = [
    { label: 'Collateral', key: 'balance' },
    { label: 'Fees due', key: 'feesDue' },
    { label: 'Penalty', key: 'penalty' },
    { label: 'Refund', key: 'refund' },
  ]
  return (
    <table className="mt-3 w-full text-sm">
      <thead><tr>
        <th className={TH}></th>
        {columns.map((c) => <th key={c.label} className={TH}>{c.label}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key}>
            <td className={`${TD} text-slate-500`}>{r.label}</td>
            {columns.map((c) => (
              <td key={c.label} className={NUM}>
                <Maybe value={usde(c.values?.[r.key] ?? null)} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
